/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import {
  EXECUTION_TYPE,
  UiController,
  breakdownList,
  commands,
  compareWindows,
  consoleCommands,
  events,
} from 'modules/common/ui-controller';
import {
  expandFocusOnAll,
  getHotelValuesFromList,
  getTrendValuesFromList,
  getValuesFromList,
  isIncluded,
} from 'modules/common/utils/array';
import { KEYS } from 'modules/dashboard/constants';
import { dashboardActions } from 'modules/dashboard/slice';
import {
  CURRENCY_STEP,
  PMS_SYNC_STEP,
  VI_RESPONSE_STEP,
} from 'modules/common/ui-controller/utils/shared-command-steps';
import { trendActions } from '../slice';

import TREND_COLLAPSE_COMMAND_STEPS from './trend-collapse-command-steps';

const isLoaded = (context) => context.isLoaded;
// focus on command with the EMPTY value clears the focus on selection
const isEmptyFocusOn = (value) => value?.length === 1 && value[0] === EXECUTION_TYPE.EMPTY;
// focus on list belongs to the selected breakdown and has options
const isFocusOnListLoaded = (context) =>
  context.isLoaded && context.isFocusOnListReady && context.focusOnList?.length > 0;
// time given to the custom compare window to open before the custom dates are applied
const CUSTOM_COMPARE_WINDOW_DELAY = 2000;
/**
 * Trend tab filter commands in the order they are applied.
 * The position in this list is the execution order, see useCommandPipeline.
 * context is built in useTrendCommandPipeline.
 */
export const TREND_FILTER_COMMAND_STEPS = [
  {
    command: consoleCommands.BOOKING_DATE,
    event: events.SET_TREND_CUSTOM_BOOKING,
    // open custom booking date picker, the date range is applied once it is opened
    prepare: (value, { dispatch }) => {
      if (value !== commands.OTB) {
        dispatch(dashboardActions.setTrendOtb(true));
        dispatch(dashboardActions.setTrendBookingStyle(true));
      }
    },
    ready: (context, { pending }) =>
      context.isLoaded &&
      (pending[consoleCommands.BOOKING_DATE] === commands.OTB || Boolean(context.customBooking)),
    apply: (value, { dispatch, resetBookingDate }) => {
      if (value === commands.OTB) {
        dispatch(dashboardActions.setTrendOtb(0));
        dispatch(dashboardActions.setTrendBookingStyle(false));
        dispatch(dashboardActions.setTrendBookingDate(resetBookingDate));
      } else {
        UiController.setBookingDateRange(value);
      }
    },
  },
  {
    command: consoleCommands.CUMULATIVE_VIEW,
    event: events.SET_TREND_CUMULATIVE_VIEW,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      dispatch(trendActions.setIsCumulative(value === 1));
    },
  },
  {
    command: consoleCommands.SEGMENT,
    event: events.SET_SEGMENT_BREAKDOWN,
    ready: (context) => context.isLoaded && context.chartBreakdownList?.length > 0,
    apply: (value, { dispatch, chartBreakdownList, resetOptions }) => {
      // get breakdown option
      const breakdownOption = breakdownList[value?.substring(0, 2)];
      // get isDetailed status from command value
      const detailsStatus = parseInt(value?.substring(2, 4), 10);
      if (breakdownOption !== undefined) {
        const option = chartBreakdownList?.filter((val) => val?.label?.includes(breakdownOption));
        if (option?.length > 0) {
          dispatch(trendActions.setTrendSegmentChartBreakdown(option[0]?.id));
        }
        // set Detailed toggle state
        if (!Number.isNaN(detailsStatus)) {
          dispatch(trendActions.setIsSegmentDetailed(detailsStatus === 1));
        }
        //
        resetOptions();
      }
    },
  },
  {
    command: consoleCommands.MOVING_AVERAGE,
    event: events.SET_TREND_MOVING_AVERAGE,
    ready: (context) => context.isLoaded && context.movingAverageList?.length > 0,
    apply: (value, { dispatch, movingAverageList }) => {
      const option = value?.substring(1);
      const status = parseInt(value?.substring(0, 1), 10);
      const averages = movingAverageList?.filter((pace) => pace?.label === Number(option));
      if (averages?.length > 0) {
        dispatch(trendActions.setIsMovingAverage(Number(status) === 1));
        dispatch(trendActions.setMovingAverage(averages[0]));
      }
    },
  },
  {
    command: consoleCommands.COMPARE_HOTEL,
    event: events.SET_TREND_HOTELS_COMPARE_HOTELS,
    ready: (context) => context.isLoaded && context.hotelList?.length > 0,
    apply: (value, { dispatch, hotelList }) => {
      const hotelsSelected = getHotelValuesFromList(value, hotelList);
      if (hotelsSelected?.length > 0) {
        dispatch(trendActions.setEnableSecondaryHotelGroups(false));
        dispatch(trendActions.setTrendHotelFocusOn(hotelsSelected[0]));
      }
    },
  },
  {
    command: consoleCommands.COMPARE_HOTEL_GROUP,
    event: events.SET_TREND_HOTEL_GROUP,
    ready: (context) => context.isLoaded && context.hotelGroupList?.length > 0,
    apply: (value, { dispatch, hotelGroupList }) => {
      const hotelGroupsSelected = getHotelValuesFromList(value, hotelGroupList);
      if (hotelGroupsSelected?.length > 0) {
        dispatch(trendActions.setEnableSecondaryHotelGroups(true));
        dispatch(trendActions.setTrendHotelFocusOn(hotelGroupsSelected[0]));
      }
    },
  },
  {
    command: consoleCommands.ALIGNMENT,
    event: events.TREND_TIME_ALIGNMENT,
    ready: (context) => context.isLoaded && Boolean(context.visualFilters),
    apply: (value, { handleAlignmentToggleChange }) => {
      handleAlignmentToggleChange({ target: { checked: value } });
    },
  },
  {
    command: consoleCommands.SYMMETRIC,
    event: events.SET_TREND_SYMMETRIC_COMPARISON,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      dispatch(trendActions.setTrendSymmetricComparisonToggle(value === 1));
    },
  },
  {
    command: consoleCommands.BREAKDOWN_TOGGLE,
    event: events.SET_TREND_BREAKDOWN_TOGGLE,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      dispatch(trendActions.setIsPrimary(value === 1));
    },
  },
  {
    // custom dates are applied by the next step, only with the custom compare window
    command: consoleCommands.COMPARE_WINDOW,
    requires: [consoleCommands.CUSTOM_DATES],
    event: events.SET_TREND_COMPARE_WINDOW,
    ready: (context) => context.isLoaded && context.dateComparison?.length > 0,
    apply: (value, { dispatch, dateComparison, setArbitraryComparisonDate }) => {
      // get compare window value
      const windowSelected = compareWindows[value];
      if (windowSelected !== undefined) {
        // checks if option is already selected
        if (dateComparison.indexOf(windowSelected) === -1) {
          dispatch(trendActions.setTrendTimeDateComparison(windowSelected));
        }
        if (windowSelected === KEYS.CUSTOM) {
          setArbitraryComparisonDate();
          return [consoleCommands.COMPARE_WINDOW];
        }
      }
      return [consoleCommands.COMPARE_WINDOW, consoleCommands.CUSTOM_DATES];
    },
    delayAfter: (value) =>
      compareWindows[value] === KEYS.CUSTOM ? CUSTOM_COMPARE_WINDOW_DELAY : 0,
  },
  {
    command: consoleCommands.CUSTOM_DATES,
    event: events.SET_TREND_TIME_CUSTOM_DATE_RANGE,
    ready: (context, { keys }) =>
      context.isLoaded &&
      (Boolean(context.dateComparison?.includes(KEYS.CUSTOM)) ||
        !keys.includes(consoleCommands.COMPARE_WINDOW)),
    apply: (value, { dispatch, dateComparison }) => {
      // check date comparison value is custom
      if (dateComparison?.includes(KEYS.CUSTOM)) {
        const datesExtracted = value?.split('-');
        dispatch(trendActions.setTrendTimeCustomDate(datesExtracted[0]));
        UiController.setCustomDateRange(value);
      }
    },
  },
  {
    command: consoleCommands.TABULAR_VIEW,
    event: events.SET_TREND_TIME_TABULAR_VIEW,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      dispatch(trendActions.setTabularView(Number(value) === 1));
    },
  },
  {
    command: consoleCommands.WEEKLY_VIEW,
    event: events.SET_TREND_WEEKLY_VIEW,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      dispatch(trendActions.setTrendTimeWeeklyView(value === 1));
    },
  },
  {
    command: consoleCommands.PERCENTAGE_TOGGLE,
    event: events.SET_TREND_PERCENTAGE_TOGGLE,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      dispatch(trendActions.setIsTrendPercentageChange(value === 1));
    },
  },
  {
    command: consoleCommands.SEGMENT_BREAKDOWN,
    event: events.SET_TREND_SEGMENT_BREAKDOWN_TOGGLE,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      dispatch(trendActions.setIsTrendDeAggregate(value === 1));
    },
  },
  {
    command: consoleCommands.FOCUS,
    event: events.SET_TREND_SEGMENT_FOCUS_ON,
    // wait until the focus on list belongs to the selected breakdown and contains the command values
    ready: (context, { pending }) => {
      const value = pending[consoleCommands.FOCUS];
      if (!isFocusOnListLoaded(context)) return false;
      if (isEmptyFocusOn(value)) return true;
      // replace ALL command value with every focus on option
      const focusOnValues = expandFocusOnAll(value, context.focusOnList);
      return Boolean(focusOnValues) && isIncluded(context.focusOnList, focusOnValues);
    },
    apply: (value, { dispatch, focusOnList }) => {
      if (isEmptyFocusOn(value)) {
        dispatch(trendActions.setTrendSegmentFocusOn([]));
        return;
      }
      // get corresponding focus on value and execute dispatch function
      const optionsSelected = getTrendValuesFromList(
        expandFocusOnAll(value, focusOnList),
        focusOnList
      );
      dispatch(
        trendActions.setTrendSegmentFocusOn(optionsSelected?.length > 0 ? optionsSelected : [])
      );
    },
  },
  {
    // custom aggregation set A, available only with the detailed breakdown
    command: consoleCommands.SET_A,
    event: events.SET_CUSTOM_AGGREGATION_A_VALUE,
    ready: isFocusOnListLoaded,
    apply: (value, { dispatch, focusOnList, isDetailed }) => {
      // get focus on options for setA
      const optionsSelected = getValuesFromList(value, focusOnList);
      if (isDetailed && optionsSelected?.length === value?.length) {
        dispatch(trendActions.setSegmentGroup1(optionsSelected));
      }
    },
  },
  {
    // custom aggregation set B, available only with the detailed breakdown
    command: consoleCommands.SET_B,
    event: events.SET_CUSTOM_AGGREGATION_B_VALUE,
    ready: isFocusOnListLoaded,
    apply: (value, { dispatch, focusOnList, isDetailed }) => {
      // get focus on options for setB
      const optionsSelected = getValuesFromList(value, focusOnList);
      if (isDetailed && optionsSelected?.length === value?.length) {
        dispatch(trendActions.setSegmentGroup2(optionsSelected));
      }
    },
  },
  {
    command: consoleCommands.FORECAST,
    event: events.SET_TREND_FORECAST,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      dispatch(trendActions.setIsTrendForecast(value === 1));
    },
  },
  {
    command: consoleCommands.NORMALIZED_VIEW,
    event: events.SET_TREND_HOTEL_NORMALIZED_VIEW,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      // execute dispatch function to set normalized view with command value
      dispatch(trendActions.setTrendHotelNormalizedView(value === 1));
    },
  },
];

/**
 * Trend tab commands of a command url in the order they are applied
 */
const TREND_COMMAND_STEPS = [
  ...TREND_FILTER_COMMAND_STEPS,
  CURRENCY_STEP,
  PMS_SYNC_STEP,
  VI_RESPONSE_STEP,
  ...TREND_COLLAPSE_COMMAND_STEPS,
];

export default TREND_COMMAND_STEPS;
